import { Calendar, CalendarChangeEvent, CalendarProps } from 'primereact/calendar';
import { classNames } from 'primereact/utils';
import React from 'react';
import { Control, Controller, ControllerRenderProps, RegisterOptions } from 'react-hook-form';
import { parse } from 'date-fns';

type RHFCalendarProps = {
    control: Control<any>;
    name: string;
    rules?: RegisterOptions;
    placeholder?: string;
    inputProps?: CalendarProps;
    onChange?: (e: CalendarChangeEvent, field: ControllerRenderProps) => void;
};

export const RHFCalendar = ({ control, name, rules, placeholder, inputProps, onChange }: RHFCalendarProps) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <Calendar
                    id={field.name}
                    {...field}
                    ref={undefined}
                    inputRef={field.ref}
                    className={classNames({ 'p-invalid': fieldState.invalid })}
                    placeholder={placeholder}
                    showIcon
                    {...inputProps}
                    {...(onChange && { onChange: (e: CalendarChangeEvent) => onChange(e, field) })}
                />
            )}
        />
    );
};

type RHFDateCalendarProps = {
    control: Control<any>;
    name: string;
    rules?: RegisterOptions;
    placeholder?: string;
    inputProps?: CalendarProps;
    afterChange?: (date: Date) => void;
};

export const RHFDateCalendar = ({ control, name, rules, placeholder, inputProps, afterChange }: RHFDateCalendarProps) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <Calendar
                    id={field.name}
                    {...field}
                    ref={undefined}
                    inputRef={field.ref}
                    className={classNames({ 'p-invalid': fieldState.invalid })}
                    placeholder={placeholder}
                    showIcon
                    {...inputProps}
                    dateFormat='d.m.yy'
                    onInput={(e: React.FormEvent<HTMLInputElement>) => {
                        if (field.value) {
                            return;
                        }

                        let value = (e.target as HTMLInputElement).value;
                        if (!value) {
                            return;
                        }

                        value = value.replaceAll(/\s/g, '');
                        const date = parse(value, 'd.M.y', new Date());

                        if (isNaN(date.getTime()) || date.getFullYear() < 1000) {
                            return;
                        }

                        field.onChange(date);
                        if (afterChange) {
                            afterChange(date);
                        }
                    }}
                    {...(afterChange && {
                        onChange: (e: CalendarChangeEvent) => {
                            field.onChange(e);
                            afterChange(e.value as Date);
                        }
                    })}
                />
            )}
        />
    );
};

export const RHFYearCalendar = ({ control, name, rules, placeholder, inputProps }: RHFDateCalendarProps) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <Calendar
                    id={field.name}
                    {...field}
                    ref={undefined}
                    inputRef={field.ref}
                    className={classNames({ 'p-invalid': fieldState.invalid })}
                    placeholder={placeholder}
                    showIcon
                    showOnFocus={false}
                    {...inputProps}
                    view='year'
                    dateFormat='yy'
                    // parse year from input text which the input doest not do by default for years
                    // FIXME: parseDateTime causes a warning in console, probably due to a bug in PrimeReact,
                    // either ignore, try switching to onInput or upgrade to a newer version of PrimeReact
                    parseDateTime={
                        ((text: string) => {
                            const year = parseInt(text);
                            if (isNaN(year) || year < 100) {
                                return null;
                            }
                            return new Date(year, 0);
                        }) as (text: string) => Date
                    }
                />
            )}
        />
    );
};
